// Native image picker (camera or gallery) via @capacitor/camera.
// Falls back to the existing <input type="file"> on web.
//
// Two entry points:
//   pickImageFile(opts)        → returns a File (or null if cancelled / web)
//   triggerImagePicker(opts)   → either runs native picker + calls the handler
//                                with a synthetic event, OR clicks the file input.
//
// Exposed globally as window._pickImageFile + window._triggerImagePicker so
// innerHTML panels (eventCreation.js, eventEdit.js) can invoke them from
// inline onclick handlers.

import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

const isNative = () => Capacitor.isNativePlatform?.() === true;

const dataUrlToFile = async (dataUrl, format) => {
  const blob = await (await fetch(dataUrl)).blob();
  const ext = (format || (blob.type.split('/')[1]) || 'jpg').toLowerCase();
  return new File([blob], `photo.${ext}`, { type: blob.type || 'image/jpeg' });
};

export const pickImageFile = async ({
  maxSize = 1200,
  quality = 85,
  allowEditing = false,
} = {}) => {
  if (!isNative()) return null;

  try {
    const photo = await Camera.getPhoto({
      quality,
      width: maxSize,
      height: maxSize,
      allowEditing,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Prompt,
      promptLabelHeader: 'Select photo',
      promptLabelPhoto: 'From gallery',
      promptLabelPicture: 'Take photo',
      promptLabelCancel: 'Cancel',
      correctOrientation: true,
    });
    if (!photo?.dataUrl) return null;
    return await dataUrlToFile(photo.dataUrl, photo.format);
  } catch (err) {
    const msg = String(err?.message || err).toLowerCase();
    if (msg.includes('cancel') || msg.includes('user cancelled')) return null;
    console.error('pickImageFile error:', err);
    return null;
  }
};

// inputId: id of the existing <input type="file"> in the DOM
// handler: optional onchange-equivalent function. If omitted, we dispatch a
//          real 'change' event on the input after setting .files via a stub.
export const triggerImagePicker = async (inputId, handler, opts) => {
  if (!isNative()) {
    document.getElementById(inputId)?.click();
    return;
  }
  const file = await pickImageFile(opts);
  if (!file) return;

  const input = document.getElementById(inputId);

  // Always try to populate input.files so any later submit-time code that
  // reads document.getElementById(inputId).files[0] still works.
  if (input) {
    try {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch (e) {
      console.warn('Could not set input.files via DataTransfer:', e);
    }
  }

  if (typeof handler === 'function') {
    handler({ target: { files: [file], value: '', ...(input ? { id: input.id } : {}) } });
    return;
  }

  if (input) {
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

if (typeof window !== 'undefined') {
  window._pickImageFile = pickImageFile;
  window._triggerImagePicker = triggerImagePicker;
  window._isNativeApp = isNative;
}
